import React, { useCallback } from 'react';
import cn from 'classnames';
import { CloseIcon } from '~/Components/Icons/CloseIcon';
import { Alert as MuiAlert, Box, Collapse } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';

import { makeStyles } from '~/UIKit/Style';
import { updateAlertVisibility, selectAlertVisibilityBy } from '~/store/alerts/alertsVisibility';
import {
  FITOGRAM_BLUE_EXTRA_LIGHT,
  PRIMARY_BLUE,
  CHIP_COLOR_RED,
  CHIP_COLOR_ORANGE,
  CHIP_COLOR_GREEN,
  WHITE,
} from '~/Constants/Colors.constants';
import { InfoIcon, AlertTriangleBold } from '~/Components/Icons';
import { FONT_SIZE_MEDIUM } from '~/Constants/UI.constants';

type Severity = 'error' | 'warning' | 'info' | 'success';

const backgroundMapping = {
  error: CHIP_COLOR_RED,
  warning: CHIP_COLOR_ORANGE,
  info: FITOGRAM_BLUE_EXTRA_LIGHT,
  success: CHIP_COLOR_GREEN,
};

const colorMapping = {
  error: WHITE,
  warning: WHITE,
  info: PRIMARY_BLUE,
  success: WHITE,
};

const useStyles = (severity: Severity, fullScreen: boolean) =>
  makeStyles(
    (theme) => ({
      EventTypePickerAlertBox: {
        [theme.breakpoints.up('lg')]: {
          paddingLeft: fullScreen ? 0 : 280,
        },
      },
      alert: {
        backgroundColor: backgroundMapping[severity],
        color: colorMapping[severity],
        borderRadius: 0,
        padding: '16px 19px',
        alignItems: 'center',
        fontSize: FONT_SIZE_MEDIUM,
        display: 'grid',
        gridTemplateColumns: '33px 2fr',
        gridTemplateAreas: `"icon message" "action action"`,
        height: 150,

        [theme.breakpoints.up('sm')]: {
          display: 'flex',
          height: 60,
        },

        button: {
          padding: '8px 15px',
          backgroundColor: WHITE,
          borderRadius: 3,
          minHeight: 35,
        },

        '& a': {
          color: colorMapping[severity],
        },
        '& .MuiAlert-icon': {
          marginRight: theme.spacing(2),
          padding: 0,
          color: colorMapping[severity],
          gridArea: 'icon',
          alignSelf: 'start',

          [theme.breakpoints.up('sm')]: {
            alignSelf: 'center',
          },
        },
        '& .MuiAlert-message': {
          textUnderlineOffset: 2,
          padding: 0,
          gridArea: 'message',
        },
        '& .MuiAlert-action': {
          padding: 0,
          marginRight: 0,
          marginTop: theme.spacing(2),
          cursor: 'pointer',
          gridArea: 'action',

          [theme.breakpoints.up('sm')]: {
            marginTop: 0,
          },
        },
      },
    }),
    { name: 'Alert' },
  );

export interface IAlertProps {
  id: string;
  message: string;
  className?: string;
  severity?: Severity;
  customAction?: JSX.Element;
  fullScreen?: boolean;
}

export const Alert = ({
  id,
  message,
  className,
  severity = 'info',
  customAction,
  fullScreen = false,
}: IAlertProps) => {
  const key = `ItemCreationAlert-${id}`;
  const open = useSelector(selectAlertVisibilityBy(key));
  const dispatch = useDispatch();
  const setOpen = bindActionCreators(updateAlertVisibility, dispatch);
  const { classes } = useStyles(severity, fullScreen)();
  const closeAlert = useCallback(() => {
    setOpen({ key, value: false });
  }, []);

  const action = customAction || (
    <CloseIcon
      className={classes.action}
      aria-label="close"
      onClick={closeAlert}
      data-testid="alert-close"
    />
  );

  return (
    <Box className={cn(classes.EventTypePickerAlertBox, className)}>
      <Collapse in={open} className={classes.collapse} id="alert-collapse">
        <MuiAlert
          className={classes.alert}
          severity={severity}
          action={action}
          iconMapping={{
            info: <InfoIcon />,
            error: <AlertTriangleBold />,
          }}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: message,
            }}
          />
        </MuiAlert>
      </Collapse>
    </Box>
  );
};
